'use client';

import { useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from '@/context/LanguageContext';
import WaveDivider from '@/components/WaveDivider';
import DoubleWaveDivider from '@/components/DoubleWaveDivider';
import styles from './page.module.scss';

interface AboutContentProps {
  profileImage: string;
  profileImagePosition: {
    x: number;
    y: number;
  };
}

export default function AboutContent({ profileImage, profileImagePosition }: AboutContentProps) {
  const { t } = useTranslation();
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const layer = parallaxRef.current;
    const section = layer?.parentElement;
    if (!layer || !section) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = section.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      const progress = (rect.top + rect.height / 2 - viewportH / 2) / viewportH;
      const shift = Math.max(-40, Math.min(40, progress * -40));
      layer.style.transform = `translate3d(0, ${shift}px, 0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <section className={`${styles.intro} section section--magenta`}>
        <div className="container">
          <div className={styles.introGrid}>
            <div className={styles.introText}>
              <h2 className={styles.sectionTitle}>{t.about.strandvaskerTitle}</h2>
              <p>
                {t.about.strandvaskerP1}
              </p>
              <p>
                {t.about.strandvaskerP2}
              </p>
            </div>
            <div className={styles.introImage}>
              <Image
                src={profileImage}
                alt={t.about.mortenAlt}
                width={600}
                height={600}
                style={{
                  objectFit: 'cover',
                  objectPosition: `${profileImagePosition.x}% ${profileImagePosition.y}%`,
                  width: '100%',
                  height: '100%',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <WaveDivider from="magenta" />

      <section className={`${styles.philosophy} section section--yellow`}>
        <div className="container">
          <div className={styles.philosophyGrid}>
            <div className={styles.philosophyVideo}>
              <video
                autoPlay
                muted
                loop
                playsInline
              >
                <source src="/media/KopOgVand_optimized.mp4" type="video/mp4" />
              </video>
            </div>
            <div className={styles.philosophyContent}>
              <h2 className={styles.sectionTitle}>{t.about.filosofiTitle}</h2>
              <blockquote className={styles.quote}>
                &ldquo;{t.about.filosofiQuote}&rdquo;
              </blockquote>
              <p>
                {t.about.filosofiP1}
              </p>
              <p>
                {t.about.filosofiP2}
              </p>
            </div>
          </div>
        </div>
      </section>

      <DoubleWaveDivider flip />

      <section className={styles.parallaxBreak} aria-label={t.about.imageAlt1}>
        <div ref={parallaxRef} className={styles.parallaxImage} aria-hidden="true" />
      </section>

      <DoubleWaveDivider />

      <section className={`${styles.roastery} section section--magenta`}>
        <div className="container">
          <div className={styles.roasteryGrid}>
            <div className={styles.roasteryContent}>
              <h2 className={styles.sectionTitle}>{t.about.risterietTitle}</h2>
              <p>{t.about.risterietP1}</p>
              <p>{t.about.risterietP2}</p>
              <p>{t.about.risterietP3}</p>
            </div>
            <div className={styles.roasteryImage}>
              <Image
                src="/media/DSCF0403_optimized.png"
                alt={t.about.imageAlt1}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                style={{ objectFit: 'cover' }}
              />
            </div>
          </div>
        </div>
      </section>

      <WaveDivider from="magenta" />

      <section className={`${styles.cta} section section--yellow`}>
        <div className="container">
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>{t.about.ctaTitle}</h2>
            <p className={styles.ctaSub}>
              {t.about.ctaBody}
            </p>
            <div className={styles.ctaButtons}>
              <Link href="/shop" className={styles.ctaBtn}>
                {t.about.ctaPrimary}
              </Link>
              <Link href="/abonnement" className={styles.ctaBtnOutline}>
                {t.about.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
